import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { bajaAlCerrarSesion } from "@/lib/pushAvisos"; // PROMPT 152: al cerrar sesion se dan de baja los avisos de este dispositivo

const AuthContext = createContext(null);
const CACHE_KEY = "tny_user_cache";

function usuarioCacheado() {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function guardarCache(user) {
  if (user) sessionStorage.setItem(CACHE_KEY, JSON.stringify(user));
  else sessionStorage.removeItem(CACHE_KEY);
}

export function AuthProvider({ children }) {
  // PROMPT 49: si hay un usuario cacheado de una sesión previa en esta pestaña,
  // se usa como estado optimista desde el arranque -- así un F5 completo con el
  // backend caído/reiniciando no muestra un parpadeo hacia /login mientras
  // /auth/me reintenta en segundo plano. Si el backend confirma con 401/403
  // (sesión realmente inválida), recién ahí se limpia.
  const [user, setUser] = useState(() => usuarioCacheado());
  const [loading, setLoading] = useState(() => !usuarioCacheado());

  const init = useCallback(async () => {
    const token = localStorage.getItem("tny_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      guardarCache(data);
    } catch (e) {
      // Error de RED o del servidor (5xx / backend reiniciando / 520 de
      // Cloudflare): NO es que la sesión sea inválida -- no hay que
      // desloguear a nadie por una falla transitoria. Solo se borra la
      // sesión si el backend respondió explícitamente 401/403.
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("tny_token");
        setUser(null);
        guardarCache(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("tny_token", data.access_token);
    setUser(data.user);
    guardarCache(data.user);
    return data.user;
  };

  const logout = async () => {
    // PROMPT 152: primero la baja de los avisos (hace falta la sesion); un error o una demora no frenan el cierre
    await Promise.race([bajaAlCerrarSesion(), new Promise((resolve) => setTimeout(resolve, 4000))]);
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem("tny_token");
    setUser(null);
    guardarCache(null);
  };

  const changePassword = async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password });
    setUser((u) => {
      const nuevo = { ...u, must_change_password: false };
      guardarCache(nuevo);
      return nuevo;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, changePassword, isAdmin: user?.rol === "admin", isAgencia: user?.rol === "agencia" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
